import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { likeProfile, unlikeProfile, saveProfile, unsaveProfile, shareProfile } from '../api/people'
import { getToken } from '../lib/auth'

type Props = {
  publicId: string
  avatarUrl?: string
  displayName?: string
  handle?: string
  counts?: { likes?: number; saves?: number; shares?: number; comments?: number }
  myState?: { liked?: boolean; saved?: boolean }
  shareUrl?: string | null
  onOpenComments?: () => void
  onGift?: () => void
  onReport?: () => void
  onMessage?: () => void
  canMessage?: boolean
  onToggleLike?: (nextLiked: boolean) => Promise<void> | void
  onToggleSave?: (nextSaved: boolean) => Promise<void> | void
  onShare?: () => Promise<void> | void
}

export default function PeopleActionRail({
  publicId,
  avatarUrl,
  counts,
  myState,
  shareUrl,
  onOpenComments,
  onGift,
  onReport,
  onMessage,
  canMessage,
  onToggleLike,
  onToggleSave,
  onShare,
}: Props) {
  const [liked, setLiked] = useState<boolean>(!!myState?.liked)
  const [saved, setSaved] = useState<boolean>(!!myState?.saved)
  const [likes, setLikes] = useState<number>(counts?.likes ?? 0)
  const [saves, setSaves] = useState<number>(counts?.saves ?? 0)
  const [shares, setShares] = useState<number>(counts?.shares ?? 0)
  const comments = counts?.comments ?? 0
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => { setLiked(!!myState?.liked) }, [myState?.liked])
  useEffect(() => { setSaved(!!myState?.saved) }, [myState?.saved])
  useEffect(() => { if (typeof counts?.likes === 'number') setLikes(counts.likes) }, [counts?.likes])
  useEffect(() => { if (typeof counts?.saves === 'number') setSaves(counts.saves) }, [counts?.saves])
  useEffect(() => { if (typeof counts?.shares === 'number') setShares(counts.shares) }, [counts?.shares])

  const authGuard = useCallback((fn: () => Promise<void>) => fn().catch((err: any) => {
    if (err?.response?.status === 401) window.location.href = '/login'
  }), [])

  const toggleLike = useCallback(() => {
    const next = !liked
    setLiked(next)
    setLikes((c) => Math.max(0, c + (next ? 1 : -1)))
    if (onToggleLike) {
      authGuard(async () => { await onToggleLike(next) })
      return
    }
    authGuard(async () => { if (next) await likeProfile(publicId); else await unlikeProfile(publicId) })
  }, [liked, publicId, authGuard, onToggleLike])

  const toggleSave = useCallback(() => {
    const next = !saved
    setSaved(next)
    setSaves((c) => Math.max(0, c + (next ? 1 : -1)))
    if (onToggleSave) {
      authGuard(async () => { await onToggleSave(next) })
      return
    }
    authGuard(async () => { if (next) await saveProfile(publicId); else await unsaveProfile(publicId) })
  }, [saved, publicId, authGuard, onToggleSave])

  const doShare = useCallback(async () => {
    if (onShare) {
      await onShare()
      setShares((c) => c + 1)
      return
    }
    const url = shareUrl || window.location.href
    try {
      if ((navigator as any).share) await (navigator as any).share({ url, title: 'Check this profile on Pixeloop' })
      else await navigator.clipboard.writeText(url)
      setShares((c) => c + 1)
      await shareProfile(publicId)
    } catch {}
  }, [publicId, shareUrl, onShare])

  const onPressMessage = useCallback(() => {
    if (!onMessage) return
    if (!getToken()) {
      window.location.href = '/login'
      return
    }
    onMessage()
  }, [onMessage])

  const IconBtn = useMemo(() => function IconBtn({ onClick, children, label }: { onClick?: () => void; children: React.ReactNode; label: string }) {
    return (
      <button onClick={onClick} aria-label={label} className="p-1.5 hover:opacity-90 transition">{children}</button>
    )
  }, [])

  return (
    <>
      <div className="hidden lg:flex fixed right-4 bottom-8 z-30 flex-col items-center gap-3">
        <div className="h-11 w-11 rounded-full overflow-hidden border border-white">
          {avatarUrl ? <img src={avatarUrl} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-white/10" />}
        </div>

        <div className="flex flex-col items-center gap-1">
          <IconBtn onClick={toggleLike} label={liked ? 'Unlike' : 'Like'}>
            <i className={`fa-solid fa-heart ${liked ? 'text-red-500' : 'text-white/95'}`} style={{ fontSize: 28 }} />
          </IconBtn>
          <div className="text-xs text-white/80">{likes}</div>
        </div>

        <div className="flex flex-col items-center gap-1">
          <IconBtn onClick={onOpenComments} label="Comments">
            <i className="fa-solid fa-comment text-white/95" style={{ fontSize: 28 }} />
          </IconBtn>
          <div className="text-xs text-white/80">{comments}</div>
        </div>

        <div className="flex flex-col items-center gap-1">
          <IconBtn onClick={toggleSave} label={saved ? 'Unsave' : 'Save'}>
            <i className={`fa-solid fa-floppy-disk ${saved ? 'text-sky-300' : 'text-white/95'}`} style={{ fontSize: 26 }} />
          </IconBtn>
          <div className="text-xs text-white/80">{saves}</div>
        </div>

        <div className="flex flex-col items-center gap-1">
          <IconBtn onClick={doShare} label="Share">
            <i className="fa-solid fa-share text-white/95" style={{ fontSize: 26 }} />
          </IconBtn>
          <div className="text-xs text-white/80">{shares}</div>
        </div>

        <div className="flex flex-col items-center gap-1">
          <IconBtn onClick={onGift} label="Send gift">
            <i className="fa-solid fa-gift" style={{ fontSize: 28, color: '#b222c5' }} />
          </IconBtn>
        </div>
        {canMessage && onMessage && (
          <div className="flex flex-col items-center gap-1">
            <IconBtn onClick={onPressMessage} label="Message">
              <i className="fa-solid fa-envelope text-white/90" style={{ fontSize: 22 }} />
            </IconBtn>
          </div>
        )}
        {onReport && (
          <div className="relative flex flex-col items-center gap-1">
            <IconBtn onClick={() => setMenuOpen((v) => !v)} label="More actions">
              <i className="fa-solid fa-ellipsis text-white/90" style={{ fontSize: 22 }} />
            </IconBtn>
            {menuOpen ? (
              <div className="absolute right-10 bottom-0 rounded-lg border border-white/15 bg-black/85 p-1 shadow-xl">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onReport?.() }}
                  className="whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-white/90 hover:bg-white/10"
                >
                  Report
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Mobile bottom bar */}
      <div className="lg:hidden fixed left-1/2 -translate-x-1/2 bottom-[calc(env(safe-area-inset-bottom)+0.75rem)] z-30 rounded-full border border-white/10 glass px-2.5 py-2 flex items-center gap-1.5">
        <button onClick={toggleLike} aria-label={liked ? 'Unlike' : 'Like'} className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10">
          <i className={`fa-solid fa-heart ${liked ? 'text-red-500' : 'text-white/95'}`} style={{ fontSize: 22 }} />
        </button>
        <button onClick={onOpenComments} aria-label="Comments" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10">
          <i className="fa-solid fa-comment text-white/95" style={{ fontSize: 22 }} />
        </button>
        <button onClick={toggleSave} aria-label={saved ? 'Unsave' : 'Save'} className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10">
          <i className={`fa-solid fa-floppy-disk ${saved ? 'text-sky-300' : 'text-white/95'}`} style={{ fontSize: 22 }} />
        </button>
        <button onClick={doShare} aria-label="Share" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10">
          <i className="fa-solid fa-share text-white/95" style={{ fontSize: 22 }} />
        </button>
        <button onClick={onGift} aria-label="Send gift" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10">
          <i className="fa-solid fa-gift" style={{ fontSize: 22, color: '#b222c5' }} />
        </button>
        {canMessage && onMessage ? (
          <button onClick={onPressMessage} aria-label="Message" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10">
            <i className="fa-solid fa-envelope text-white/90" style={{ fontSize: 20 }} />
          </button>
        ) : null}
        {onReport ? (
          <div className="relative">
            <button onClick={() => setMenuOpen((v) => !v)} aria-label="More actions" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10">
              <i className="fa-solid fa-ellipsis text-white/90" style={{ fontSize: 20 }} />
            </button>
            {menuOpen ? (
              <div className="absolute right-0 bottom-10 rounded-lg border border-white/15 bg-black/85 p-1 shadow-xl">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onReport?.() }}
                  className="whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-white/90 hover:bg-white/10"
                >
                  Report
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  )
}
